import React from "react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Search, X, Download, Calendar } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Calendar as CalendarComponent } from "@/shared/components/ui/calendar";
import { format } from "date-fns";

/* ─────────────────────────────────────────────────────────────────
   ALL values hardcoded in hex — zero CSS custom properties used
   anywhere that renders in a Radix portal (dropdown, popover).
   This is the only reliable way to style portal content.
───────────────────────────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');

  /* wrapper */
  .tf-root {
    background: #0E1C35;
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 0.875rem;
    padding: 0.875rem 1rem 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    font-family: 'Outfit', system-ui, sans-serif;
    transition: border-color 150ms ease;
  }
  .tf-root:hover { border-color: rgba(34,211,238,0.18); }

  /* row */
  .tf-row { display: flex; flex-wrap: wrap; gap: 0.45rem; align-items: center; }

  /* search */
  .tf-sw { position: relative; flex: 1; min-width: 180px; }
  .tf-si {
    position: absolute; left: 0.65rem; top: 50%; transform: translateY(-50%);
    width: 0.8rem; height: 0.8rem; color: #4D6E90; pointer-events: none;
    transition: color 120ms ease;
  }
  .tf-sw:focus-within .tf-si { color: #22D3EE; }
  .tf-input {
    width: 100%; height: 2.125rem;
    padding: 0 0.75rem 0 2rem;
    background: #0A1628;
    border: 1px solid rgba(255,255,255,0.09);
    border-radius: 0.55rem;
    color: #EFF6FF;
    font-size: 0.8125rem;
    font-family: 'Outfit', system-ui, sans-serif;
    outline: none;
    transition: border-color 120ms ease, background 120ms ease;
  }
  .tf-input::placeholder { color: #4D6E90; }
  .tf-input:hover  { border-color: rgba(34,211,238,0.22); background: #122040; }
  .tf-input:focus  { border-color: #22D3EE; box-shadow: 0 0 0 2px rgba(34,211,238,0.14); background: #122040; }

  /* select triggers — scoped to .tf-root to override Radix defaults */
  .tf-root [data-radix-select-trigger],
  .tf-root button[role="combobox"] {
    height: 2.125rem !important;
    background: #0A1628 !important;
    border: 1px solid rgba(255,255,255,0.09) !important;
    border-radius: 0.55rem !important;
    color: #EFF6FF !important;
    font-size: 0.8125rem !important;
    font-family: 'Outfit', system-ui, sans-serif !important;
    padding: 0 0.55rem 0 0.7rem !important;
    transition: border-color 120ms ease, background 120ms ease !important;
    cursor: pointer !important;
  }
  .tf-root [data-radix-select-trigger]:hover,
  .tf-root button[role="combobox"]:hover {
    border-color: rgba(34,211,238,0.25) !important;
    background: #122040 !important;
    color: #EFF6FF !important;
  }
  .tf-root [data-radix-select-trigger][data-state="open"],
  .tf-root button[role="combobox"][data-state="open"] {
    border-color: #22D3EE !important;
    box-shadow: 0 0 0 2px rgba(34,211,238,0.15) !important;
    background: #122040 !important;
  }
  .tf-root [data-radix-select-trigger] > span,
  .tf-root button[role="combobox"] > span { color: #EFF6FF !important; }
  .tf-root [data-radix-select-trigger] svg,
  .tf-root button[role="combobox"] svg {
    color: #4D6E90 !important;
    width: 0.8rem !important; height: 0.8rem !important;
    flex-shrink: 0 !important;
    transition: color 120ms ease !important;
  }
  .tf-root [data-radix-select-trigger]:hover svg,
  .tf-root button[role="combobox"]:hover svg { color: #22D3EE !important; }

  /* ── SELECT DROPDOWN — portal-safe, all hardcoded ── */
  [data-radix-select-content] {
    background: #0A1628 !important;
    border: 1px solid rgba(255,255,255,0.10) !important;
    border-radius: 0.75rem !important;
    box-shadow: 0 16px 48px rgba(0,0,0,0.7) !important;
    padding: 0.25rem !important;
    z-index: 9999 !important;
  }
  [data-radix-select-viewport] { padding: 0 !important; }
  [data-radix-select-item] {
    color: #93B4D4 !important;
    font-size: 0.8rem !important;
    font-weight: 500 !important;
    font-family: 'Outfit', system-ui, sans-serif !important;
    border-radius: 0.45rem !important;
    padding: 0.45rem 0.75rem !important;
    cursor: pointer !important;
    transition: background 100ms ease, color 100ms ease !important;
    outline: none !important;
    user-select: none !important;
  }
  [data-radix-select-item][data-highlighted] {
    background: rgba(34,211,238,0.11) !important;
    color: #22D3EE !important;
  }
  [data-radix-select-item][data-state="checked"] {
    background: rgba(34,211,238,0.15) !important;
    color: #22D3EE !important;
    font-weight: 700 !important;
  }
  [data-radix-select-item-indicator] { color: #22D3EE !important; }
  [data-radix-select-item-indicator] svg { color: #22D3EE !important; width: 0.75rem !important; height: 0.75rem !important; }
  [data-radix-select-separator] { background: rgba(255,255,255,0.06) !important; margin: 0.2rem 0 !important; }

  /* ── DATE BUTTON ── */
  .tf-date {
    display: flex; align-items: center; gap: 0.35rem;
    height: 2.125rem; padding: 0 0.65rem; min-width: 112px;
    border-radius: 0.55rem;
    border: 1px solid rgba(255,255,255,0.09);
    background: #0A1628;
    color: #93B4D4;
    font-size: 0.8rem; font-family: 'Outfit', system-ui, sans-serif;
    cursor: pointer; white-space: nowrap;
    transition: border-color 120ms ease, background 120ms ease, color 120ms ease;
  }
  .tf-date svg { width: 0.75rem; height: 0.75rem; color: #4D6E90; flex-shrink: 0; transition: color 120ms ease; }
  .tf-date:hover { border-color: rgba(34,211,238,0.25); background: #122040; color: #EFF6FF; }
  .tf-date:hover svg { color: #22D3EE; }
  .tf-date-on {
    border-color: rgba(34,211,238,0.38) !important;
    background: rgba(34,211,238,0.08) !important;
    color: #22D3EE !important;
  }
  .tf-date-on svg { color: #22D3EE !important; }

  /* ── CALENDAR POPOVER — portal-safe, all hardcoded ── */
  [data-radix-popover-content] {
    background: #0A1628 !important;
    border: 1px solid rgba(255,255,255,0.10) !important;
    border-radius: 0.875rem !important;
    box-shadow: 0 16px 48px rgba(0,0,0,0.7) !important;
    padding: 0.5rem !important;
    color: #93B4D4 !important;
    z-index: 9999 !important;
  }
  [data-radix-popover-content] * { font-family: 'Outfit', system-ui, sans-serif !important; }
  [data-radix-popover-content] button {
    color: #93B4D4 !important;
    background: transparent !important;
    border-radius: 0.4rem !important;
    font-size: 0.8rem !important;
    transition: background 100ms ease, color 100ms ease !important;
  }
  [data-radix-popover-content] button:hover {
    background: rgba(34,211,238,0.10) !important;
    color: #22D3EE !important;
  }
  [data-radix-popover-content] [aria-selected="true"],
  [data-radix-popover-content] [data-selected] {
    background: rgba(34,211,238,0.20) !important;
    color: #22D3EE !important;
    font-weight: 700 !important;
  }
  [data-radix-popover-content] th {
    color: #4D6E90 !important;
    font-size: 0.65rem !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.06em !important;
  }
  [data-radix-popover-content] [class*="caption"],
  [data-radix-popover-content] [class*="month"] h2,
  [data-radix-popover-content] [class*="nav"] {
    color: #EFF6FF !important;
    font-weight: 700 !important;
  }

  /* ── ACTIONS BAR ── */
  .tf-actions {
    display: flex; align-items: center; justify-content: space-between;
    padding-top: 0.5rem;
    border-top: 1px solid rgba(255,255,255,0.06);
  }
  .tf-clear {
    display: inline-flex; align-items: center; gap: 0.3rem;
    padding: 0.3rem 0.55rem; border-radius: 0.4rem;
    border: 1px solid transparent; background: transparent;
    color: #4D6E90; font-size: 0.775rem;
    font-family: 'Outfit', system-ui, sans-serif;
    cursor: pointer;
    transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
  }
  .tf-clear svg { width: 0.725rem; height: 0.725rem; }
  .tf-clear:hover { background: rgba(251,113,133,0.08); border-color: rgba(251,113,133,0.20); color: #FB7185; }

  .tf-export {
    display: inline-flex; align-items: center; gap: 0.35rem;
    padding: 0.35rem 0.8rem; border-radius: 0.5rem;
    border: 1px solid rgba(16,185,129,0.25); background: rgba(16,185,129,0.08);
    color: #34D399; font-size: 0.8rem; font-weight: 700;
    font-family: 'Outfit', system-ui, sans-serif; cursor: pointer;
    transition: background 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
  }
  .tf-export svg { width: 0.725rem; height: 0.725rem; }
  .tf-export:hover { background: rgba(16,185,129,0.15); border-color: rgba(16,185,129,0.42); box-shadow: 0 0 10px rgba(16,185,129,0.14); }
`;

export default function TransactionFilters({ filters, onFilterChange, merchants = [], providers = [], onExport, onReset }) {
  const { t } = useLanguage();
  const set = (k, v) => onFilterChange({ ...filters, [k]: v });

  const df = filters.dateFrom ? new Date(filters.dateFrom) : undefined;
  const dt = filters.dateTo ? new Date(filters.dateTo) : undefined;

  return (
    <>
      <style>{CSS}</style>
      <div className="tf-root">

        <div className="tf-row">
          {/* Search */}
          <div className="tf-sw">
            <Search className="tf-si" />
            <input className="tf-input" placeholder={t("searchIdRefMerchant")}
              value={filters.search || ""}
              onChange={(e) => set("search", e.target.value)} />
          </div>

          {/* Status */}
          <Select
            value={filters.status || "all"}
            onValueChange={(v) => set("status", v)}
          >
            <SelectTrigger
              className="w-[132px] h-8 bg-[#0A1628] border border-white/10 
               text-white hover:border-cyan-400 
               hover:shadow-[0_0_8px_rgba(34,211,238,0.4)]
               focus:ring-2 focus:ring-cyan-400"
            >
              <SelectValue placeholder={t("status")} />
            </SelectTrigger>

            <SelectContent
              className="bg-[#0A1628] border border-white/10 
               text-white rounded-lg shadow-2xl"
            >
              <SelectItem
                value="all"
                className="text-white hover:bg-cyan-500/20 
                 focus:bg-cyan-500/20 focus:text-cyan-400"
              >
                {t("allStatus")}
              </SelectItem>

              <SelectItem
                value="pending"
                className="text-white hover:bg-cyan-500/20 
                 focus:bg-cyan-500/20 focus:text-cyan-400"
              >
                {t("pending")}
              </SelectItem>

              {/* <SelectItem
                value="processing"
                className="text-white hover:bg-cyan-500/20 
                 focus:bg-cyan-500/20 focus:text-cyan-400"
              >
                {t("processing")}
              </SelectItem> */}

              <SelectItem
                value="success"
                className="text-white hover:bg-cyan-500/20 
                 focus:bg-cyan-500/20 focus:text-cyan-400"
              >
                {t("success")}
              </SelectItem>

              {/* <SelectItem
                value="declined"
                className="text-white hover:bg-cyan-500/20 
                 focus:bg-cyan-500/20 focus:text-cyan-400"
              >
                {t("failed")}
              </SelectItem> */}

              {/* <SelectItem
                value="expired"
                className="text-white hover:bg-cyan-500/20 
                 focus:bg-cyan-500/20 focus:text-cyan-400"
              >
                {t("expired")}
              </SelectItem> */}

              <SelectItem
                value="declined"
                className="text-white hover:bg-cyan-500/20 
                 focus:bg-cyan-500/20 focus:text-cyan-400"
              >
                {t("declined")}
              </SelectItem>
            </SelectContent>
          </Select>



          {/* Date From */}
          <Popover>
            <PopoverTrigger asChild>
              <button className={`tf-date${df ? " tf-date-on" : ""}`}>
                <Calendar /><span>{df ? format(df, "MMM d, yyyy") : t("from") || "From"}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent style={{ width: "auto", padding: 0 }} align="start">
              <CalendarComponent mode="single" selected={df}
                onSelect={(d) => onFilterChange({ ...filters, dateFrom: d ? d.toISOString() : undefined })} />
            </PopoverContent>
          </Popover>

          {/* Date To */}
          <Popover>
            <PopoverTrigger asChild>
              <button className={`tf-date${dt ? " tf-date-on" : ""}`}>
                <Calendar /><span>{dt ? format(dt, "MMM d, yyyy") : t("to") || "To"}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent style={{ width: "auto", padding: 0 }} align="start">
              <CalendarComponent mode="single" selected={dt}
                onSelect={(d) => onFilterChange({ ...filters, dateTo: d ? d.toISOString() : undefined })} />
            </PopoverContent>
          </Popover>
        </div>

        <div className="tf-actions">
          <button className="tf-clear" onClick={onReset}><X />{t("clearFilters")}</button>
          <button className="tf-export" onClick={onExport}><Download />{t("exportCsv")}</button>
        </div>
      </div>
    </>
  );
}